import type { CartLine, Money } from '@repo/shopify';
import { isApparelProduct, normalizeProductType } from '@/lib/apparel-type';

/**
 * Apparel "Bundle & Save" — buy 3 tees or hoodies, every one is priced at 20%
 * off MSRP; buy 4, 25%. "MSRP" is the compare-at price (the selling price when
 * there is none): a tee already 15% off gets the remaining 10 points, a
 * full-price hoodie gets the whole 25%. Renders as a strip under the
 * add-to-cart button; the cart is the bundle (nothing to pick, adding to the
 * cart moves the ladder).
 *
 * Which products count is `isApparelProduct` (product type + a short
 * exclusion list, in code). Checkout enforcement is the
 * `apparel-bundle-discount` Shopify Function in apps/shopify-functions, which
 * carries the same rule and rungs — a parity test keeps the two identical.
 * The CMS owns the on/off switch, the copy, and WHERE the strip shows
 * (`showOnProductTypes`) — never what counts, so it can't promise more than
 * checkout gives.
 */

export interface ApparelBundleRung {
	/** Eligible apparel items in the cart required to reach this rung. */
	items: number;
	/** TOTAL percent off MSRP every counted item is priced at once reached. */
	percentOff: number;
	/** Badge, e.g. "25% OFF". */
	badge: string;
}

export interface ApparelBundleSettings {
	slotCount: number;
	minItems: number;
	rungs: ApparelBundleRung[];
	/** Strip heading, e.g. "Buy more, save more". */
	heading: string;
	/** Subline under the heading, e.g. "Mix & match any hoodie or tee". */
	subheading: string;
	/**
	 * Product types whose PDPs show the strip. Empty means every product on
	 * the ladder. Display only — cart lines count by the built-in rule.
	 */
	showOnProductTypes: string[];
}

export const DEFAULT_APPAREL_BUNDLE_SETTINGS: ApparelBundleSettings = {
	slotCount: 4,
	minItems: 3,
	rungs: [
		{ items: 3, percentOff: 20, badge: '20% OFF' },
		{ items: 4, percentOff: 25, badge: '25% OFF' },
	],
	heading: 'Buy more, save more',
	subheading: 'Mix & match any hoodie or tee',
	showOnProductTypes: [],
};

/** Shopper-facing title of the automatic discount; allocations are matched on it. */
export const APPAREL_BUNDLE_DISCOUNT_TITLE = 'Bundle & Save';

/** What a PDP receives. */
export interface ApparelBundleOffer {
	config: ApparelBundleSettings;
}

function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

/**
 * What one unit costs at a rung: the rung's percent off MSRP — the compare-at
 * price, or the selling price when there is none — and never more than it
 * sells for today. Mirror of `getUnitPrice` in the Function.
 */
export function getRungUnitPrice(
	price: number,
	compareAt: number | null,
	percentOff: number,
): number {
	if (price <= 0) return 0;
	const list = compareAt != null && compareAt > price ? compareAt : price;
	return round2(Math.min(price, list * (1 - percentOff / 100)));
}

/** Per-unit saving at a rung: today's price less the rung price, floored at zero. */
export function getRungUnitSaving(
	price: number,
	compareAt: number | null,
	percentOff: number,
): number {
	if (price <= 0 || percentOff <= 0) return 0;
	return round2(Math.max(0, price - getRungUnitPrice(price, compareAt, percentOff)));
}

/**
 * Whether a unit can earn at a rung — only then does it count toward one.
 * Judged at the entry rung, so an item already cheaper than the ladder's
 * first step can't be used to bump everything else up for free.
 */
export function earnsAtRung(price: number, compareAt: number | null, percentOff: number): boolean {
	return getRungUnitSaving(price, compareAt, percentOff) > 0;
}

export function getEntryRungPercent(config: ApparelBundleSettings): number {
	return config.rungs[0]?.percentOff ?? 0;
}

export function getRungForCount(
	rungs: ApparelBundleRung[],
	count: number,
): { rung: ApparelBundleRung | null; nextRung: ApparelBundleRung | null } {
	const sorted = [...rungs].sort((a, b) => a.items - b.items);
	let rung: ApparelBundleRung | null = null;
	let nextRung: ApparelBundleRung | null = null;
	for (const candidate of sorted) {
		if (count >= candidate.items) rung = candidate;
		else if (!nextRung) nextRung = candidate;
	}
	return { rung, nextRung };
}

function toAmount(money: Money | null | undefined): number | null {
	if (!money) return null;
	const parsed = Number(money.amount);
	return Number.isFinite(parsed) ? parsed : null;
}

export interface ApparelBundleProductIdentity {
	handle?: string | null;
	title?: string | null;
	productType: string;
}

/** Whether a product is on the ladder: the built-in apparel rule (type + exclusions). */
export function isBundleProduct(
	_config: ApparelBundleSettings,
	product: ApparelBundleProductIdentity,
): boolean {
	return isApparelProduct(product);
}

/** Whether the strip shows on this product's PDP: on the ladder, and in the CMS show-on list when one is set. */
export function showsBundleStrip(
	config: ApparelBundleSettings,
	product: ApparelBundleProductIdentity,
): boolean {
	if (!isBundleProduct(config, product)) return false;
	if (config.showOnProductTypes.length === 0) return true;
	const type = normalizeProductType(product.productType);
	return config.showOnProductTypes.some((entry) => normalizeProductType(entry) === type);
}

/** Whether a catalog product shows the strip (and counts toward a rung). */
export function isBundleEligibleProduct(
	config: ApparelBundleSettings,
	input: ApparelBundleProductIdentity & {
		price: Money | null | undefined;
		compareAtPrice?: Money | null | undefined;
		availableForSale: boolean;
		requiresSellingPlan?: boolean;
	},
): boolean {
	if (!input.availableForSale || input.requiresSellingPlan) return false;
	if (!isBundleProduct(config, input)) return false;
	const price = toAmount(input.price);
	if (price == null) return false;
	return earnsAtRung(price, toAmount(input.compareAtPrice), getEntryRungPercent(config));
}

/** Line attributes that mark a cart line as belonging to another mechanic entirely. */
export const APPAREL_BUNDLE_FOREIGN_ATTRIBUTE_KEYS = [
	'_free_gift',
	'_kit_contents',
	'_hydrate_bundle',
];

export type ApparelBundleCartLine = Pick<CartLine, 'merchandise' | 'cost'> &
	Partial<
		Pick<
			CartLine,
			'id' | 'quantity' | 'attributes' | 'sellingPlanAllocation' | 'discountAllocations'
		>
	>;

function getLinePricing(
	line: ApparelBundleCartLine,
): { price: number; compareAt: number | null } | null {
	const price = toAmount(line.cost?.amountPerQuantity ?? line.merchandise?.price);
	if (price == null) return null;
	return { price, compareAt: toAmount(line.cost?.compareAtAmountPerQuantity) };
}

/**
 * Whether a cart line counts toward a rung — the Function's rule: a paid,
 * one-time unit of an apparel product. Subscriptions, reward lines, Crew Pack
 * kits and package protection are skipped; they're priced by mechanics that
 * own them.
 */
export function isApparelBundleCartLine(
	config: ApparelBundleSettings,
	line: ApparelBundleCartLine,
): boolean {
	if (line.sellingPlanAllocation) return false;
	if (
		line.attributes?.some((attribute) =>
			APPAREL_BUNDLE_FOREIGN_ATTRIBUTE_KEYS.includes(attribute.key),
		)
	) {
		return false;
	}
	const product = line.merchandise?.product;
	if (!product || !isBundleProduct(config, product)) return false;
	const pricing = getLinePricing(line);
	return (
		pricing != null && earnsAtRung(pricing.price, pricing.compareAt, getEntryRungPercent(config))
	);
}

export interface ApparelBundleCartProjection {
	/** Eligible units in the cart (quantities count). */
	units: number;
	rung: ApparelBundleRung | null;
	nextRung: ApparelBundleRung | null;
	remainingToNext: number;
	/** What the reached rung takes off the cart, before Shopify has applied it. */
	saving: number;
	currencyCode: string;
	/** Per-unit saving by cart line id — what each line is worth after the ladder. */
	unitSavingByLineId: Record<string, number>;
}

/** The ladder as it stands in this cart — what the Hydrate perk projects from. */
export function projectApparelBundle(
	config: ApparelBundleSettings,
	lines: ApparelBundleCartLine[],
): ApparelBundleCartProjection {
	const eligible = lines.filter((line) => isApparelBundleCartLine(config, line));
	const units = eligible.reduce((sum, line) => sum + Math.max(1, line.quantity ?? 1), 0);
	const { rung, nextRung } = getRungForCount(config.rungs, units);

	let saving = 0;
	let currencyCode = '';
	const unitSavingByLineId: Record<string, number> = {};
	if (rung) {
		for (const line of eligible) {
			const pricing = getLinePricing(line);
			if (!pricing) continue;
			const unitSaving = getRungUnitSaving(pricing.price, pricing.compareAt, rung.percentOff);
			saving += unitSaving * Math.max(1, line.quantity ?? 1);
			if (line.id) unitSavingByLineId[line.id] = unitSaving;
			currencyCode ||= line.cost.amountPerQuantity.currencyCode;
		}
	}

	return {
		units,
		rung,
		nextRung,
		remainingToNext: nextRung ? Math.max(0, nextRung.items - units) : 0,
		saving: round2(saving),
		currencyCode: currencyCode || lines[0]?.cost.amountPerQuantity.currencyCode || 'USD',
		unitSavingByLineId,
	};
}

/** The ladder as Shopify applied it: allocations titled "Bundle & Save", summed. */
export function getAppliedApparelBundle(
	lines: Partial<Pick<CartLine, 'discountAllocations'>>[],
): Money | null {
	const needle = APPAREL_BUNDLE_DISCOUNT_TITLE.toLowerCase();
	let total = 0;
	let currencyCode = '';
	for (const line of lines) {
		for (const allocation of line.discountAllocations ?? []) {
			if (!(allocation.title ?? '').toLowerCase().includes(needle)) continue;
			total += Number.parseFloat(allocation.discountedAmount.amount) || 0;
			currencyCode ||= allocation.discountedAmount.currencyCode;
		}
	}
	if (total <= 0) return null;
	return { amount: round2(total).toFixed(2), currencyCode };
}

/** Raw `apparelBundle` group from the product-page-settings global. */
export interface ApparelBundleCmsSettings {
	enabled?: boolean | null;
	heading?: string | null;
	subheading?: string | null;
	rungs?: { items?: number | null; percentOff?: number | null }[] | null;
	showOnProductTypes?: { productType?: string | null }[] | null;
}

/**
 * Preview override: `NEXT_PUBLIC_APPAREL_BUNDLE=1` forces the strip on. Local
 * dev is on by default.
 */
function isApparelBundleForced(): boolean {
	if (process.env.NEXT_PUBLIC_APPAREL_BUNDLE === '1') return true;
	return process.env.NODE_ENV === 'development';
}

/**
 * CMS group → settings, with defaults for anything unset. Returns null when
 * the strip is off: the CMS toggle is the switch, the env override the escape
 * hatch. Rungs are sorted by item count and must have both numbers.
 */
export function resolveApparelBundleSettings(
	raw: ApparelBundleCmsSettings | null | undefined,
): ApparelBundleSettings | null {
	if (!raw?.enabled && !isApparelBundleForced()) return null;
	const defaults = DEFAULT_APPAREL_BUNDLE_SETTINGS;

	const rungs = (raw?.rungs ?? [])
		.filter(
			(rung): rung is { items: number; percentOff: number } =>
				typeof rung?.items === 'number' &&
				typeof rung?.percentOff === 'number' &&
				rung.items >= 2 &&
				rung.percentOff > 0,
		)
		.map((rung) => ({
			items: rung.items,
			percentOff: rung.percentOff,
			badge: `${rung.percentOff}% OFF`,
		}))
		.sort((a, b) => a.items - b.items);
	const effectiveRungs = rungs.length > 0 ? rungs : defaults.rungs;

	const showOnProductTypes = (raw?.showOnProductTypes ?? [])
		.map((entry) => entry?.productType?.trim() ?? '')
		.filter(Boolean);

	return {
		slotCount: Math.max(defaults.slotCount, effectiveRungs[effectiveRungs.length - 1]?.items ?? 0),
		minItems: effectiveRungs[0]?.items ?? defaults.minItems,
		rungs: effectiveRungs,
		heading: raw?.heading?.trim() || defaults.heading,
		subheading: raw?.subheading?.trim() || defaults.subheading,
		showOnProductTypes,
	};
}

/**
 * Whether this PDP offers the strip: the product is on the ladder (the same
 * rule its cart lines are counted by), priced where the entry rung lowers
 * it, and its type is in the CMS show-on list when one is set.
 */
export function resolveApparelBundleScope(
	config: ApparelBundleSettings | null,
	input: ApparelBundleProductIdentity & {
		price: Money | null | undefined;
		compareAtPrice?: Money | null | undefined;
		availableForSale: boolean;
		requiresSellingPlan?: boolean;
	},
): ApparelBundleSettings | null {
	if (!config) return null;
	if (!showsBundleStrip(config, input)) return null;
	return isBundleEligibleProduct(config, input) ? config : null;
}
